<template>
  <div class="edit-perfil-container">

    <!-- HEADER -->
    <div class="edit-perfil-header">
      <button class="btn-back" @click="goBack">
        <i class="material-icons">arrow_back</i>
      </button>
      <h1>Editar Perfil</h1>
    </div>

    <!-- SKELETON LOADER -->
    <div v-if="pageLoading" class="skeleton-wrapper">
      <div class="skeleton-form">
        <div class="skeleton-input" v-for="n in 5" :key="n"></div>
        <div class="skeleton-button"></div>
      </div>
    </div>

    <!-- CONTENT -->
    <div v-else class="edit-perfil-content">
      <form @submit.prevent="updateProfile">

        <!-- NOMBRE -->
        <div class="form-group">
          <label>Nombre</label>
          <input type="text" v-model="form.first_name" placeholder="Tu nombre">
          <small v-if="errors.first_name" class="error">{{ errors.first_name }}</small>
        </div>

        <!-- APELLIDO -->
        <div class="form-group">
          <label>Apellido</label>
          <input type="text" v-model="form.last_name" placeholder="Tu apellido">
          <small v-if="errors.last_name" class="error">{{ errors.last_name }}</small>
        </div>

        <!-- EMAIL -->
        <div class="form-group">
          <label>Correo electrónico</label>
          <input type="email" v-model="form.email" placeholder="tu@email.com">
          <small v-if="errors.email" class="error">{{ errors.email }}</small>
        </div>

        <!-- TELÉFONO -->
        <div class="form-group">
          <label>Teléfono</label>
          <input type="tel" v-model="form.phone" placeholder="Teléfono">
          <small v-if="errors.phone" class="error">{{ errors.phone }}</small>
        </div>

        <!-- GÉNERO -->
        <div class="form-group">
          <label>Género</label>
          <select v-model="form.gender">
            <option value="">Seleccionar</option>
            <option value="Hombre">Hombre</option>
            <option value="Mujer">Mujer</option>
          </select>
          <small v-if="errors.gender" class="error">{{ errors.gender }}</small>
        </div>

        <!-- BOTÓN -->
        <button type="submit" class="btn-save" :disabled="loading">

          <div v-if="loading" class="loader-btn">
            <div class="spinner"></div>
            <span>Guardando...</span>
          </div>

          <span v-else>Guardar cambios</span>

        </button>

      </form>
    </div>

    <!-- MODAL ÉXITO -->
    <div v-if="showModal" class="modal-overlay">
  <div class="modal" :class="modalType">

    <div class="modal-icon">
      <span v-if="modalType === 'success'">✔</span>
      <span v-else-if="modalType === 'premium'">🔒</span>
      <span v-else>⚠</span>
    </div>

    <h2>{{ modalTitle }}</h2>
    <p>{{ modalMessage }}</p>

    <button class="modal-btn" @click="closeModal">
      Aceptar
    </button>

  </div>
</div>

  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api'
import { useAuthStore } from '../../stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const loading = ref(false)
const showModal = ref(false)
const pageLoading = ref(true) // 👈 NUEVO

const modalTitle = ref('')
const modalMessage = ref('')
const modalType = ref('success')

const user = ref(null)

const form = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  gender: ''
})

const errors = ref({})

const validateForm = () => {
  errors.value = {}

  if (!form.value.first_name) errors.value.first_name = 'El nombre es obligatorio'
  if (!form.value.last_name) errors.value.last_name = 'El apellido es obligatorio'
  if (!form.value.email) errors.value.email = 'El correo es obligatorio'
  else if (!/\S+@\S+\.\S+/.test(form.value.email)) errors.value.email = 'Correo inválido'
  if (!form.value.phone) errors.value.phone = 'El teléfono es obligatorio'
  if (!form.value.gender) errors.value.gender = 'Selecciona un género'

  return Object.keys(errors.value).length === 0
}

const loadUser = () => {
  pageLoading.value = true

  setTimeout(() => {
    const storedUser = localStorage.getItem('user')

    if (storedUser) {
      const parsed = JSON.parse(storedUser)

      user.value = parsed

      form.value = {
        first_name: parsed.first_name || '',
        last_name: parsed.last_name || '',
        email: parsed.email || '',
        phone: parsed.phone || '',   // 👈 YA GARANTIZADO
        gender: parsed.gender || ''
      }
    }

    pageLoading.value = false
  }, 600)
}

const updateProfile = async () => {
  if (!validateForm()) return

  loading.value = true

  try {
    const { data } = await api.put('/app/profile', form.value)

    const updatedUser = {
      ...user.value,
      ...data?.user
    }

    authStore.user = updatedUser
    localStorage.setItem('user', JSON.stringify(updatedUser))

    showModal.value = true

  } catch (error) {
  console.error(error)

  const response = error?.response?.data

  modalTitle.value = response?.requires_premium
    ? 'Acceso restringido'
    : 'Error'

  modalMessage.value =
    response?.message || 'Error al actualizar'

  modalType.value = response?.requires_premium
    ? 'premium'
    : 'error'

  showModal.value = true
}finally {
    loading.value = false
  }
}

const closeModal = () => {
  showModal.value = false
  router.back()
}

const goBack = () => {
  router.back()
}

onMounted(() => {
  loadUser()
})
</script>


<style scoped>
.edit-perfil-container {
  min-height: 100vh;
  background: #ffffff; /* blanco como pediste */
}

/* HEADER */
.edit-perfil-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.btn-back {
  background: none;
  border: none;
  cursor: pointer;
}

.btn-back i {
  font-size: 26px;
  color: #00144a;
  margin-top: 3px;
}

.edit-perfil-header h1 {
    font-size: 18px;
}

/* FORM */
.edit-perfil-content {
  padding: 20px;
  max-width: 600px;
  margin: auto;
}

.form-group {
  margin-bottom: 18px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #00144a;
  outline: none;
}

/* ERRORES */
.error {
  color: #e53935;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

/* BOTÓN AZUL */
.btn-save {
  width: 100%;
  padding: 14px;
  background: #00144a;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.btn-save:hover {
  background: #002a73;
}

.btn-save:disabled {
  opacity: 0.7;
}

/* LOADER */
.loader-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 3px solid #ccc;
  border-top: 3px solid white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* MODAL */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal {
  background: white;
  padding: 25px;
  border-radius: 14px;
  text-align: center;
  width: 300px;
}

.modal-icon {
  font-size: 40px;
  color: #00c853;
}

.modal-btn {
  margin-top: 10px;
  background: #00144a;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
}
/* =========================
   SKELETON LOADER
========================= */

.skeleton-wrapper {
  padding: 20px;
  max-width: 600px;
  margin: auto;
}

.skeleton-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-input {
  height: 45px;
  background: linear-gradient(90deg, #eee 25%, #f5f5f5 50%, #eee 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
  border-radius: 8px;
}

.skeleton-button {
  height: 50px;
  background: linear-gradient(90deg, #ddd 25%, #eee 50%, #ddd 75%);
  background-size: 200% 100%;
  animation: shimmer 1.3s infinite;
  border-radius: 10px;
  margin-top: 10px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.modal.success .modal-icon {
  color: #00c853;
}

.modal.error .modal-icon {
  color: #e53935;
}

.modal.premium .modal-icon {
  color: #ff9800;
}
</style>